import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save, X, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Header } from "@/pages/admin/AdminDashboard";
import { Card, Field } from "@/pages/admin/AdminProfile";

const empty = {
    company: "",
    role: "",
    location: "",
    period_start: "",
    period_end: "",
    current: false,
    description: "",
    achievements: [],
    tech: [],
    order: 0,
};

export default function AdminExperiences() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        const res = await api.get("/experiences");
        setItems(res.data);
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this experience?")) return;
        await api.delete(`/admin/experiences/${id}`);
        toast.success("Experience deleted");
        load();
    };

    return (
        <div data-testid="admin-experiences-page">
            <Header
                title="Experiences"
                subtitle="Riwayat pekerjaan, pencapaian, dan stack yang dipakai di tiap peran."
                action={
                    <button onClick={() => setEditing({ ...empty })} data-testid="exp-new" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-brand text-white hover:bg-brand-hover">
                        <Plus size={16} /> New experience
                    </button>
                }
            />

            {loading ? (
                <div className="mono-label">Loading…</div>
            ) : (
                <div className="space-y-3">
                    {items.map((e) => (
                        <div key={e.id} className="p-5 rounded-xl border border-white/5 bg-ink-800/40 flex items-start justify-between gap-4">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="font-display text-lg text-white">{e.role}</span>
                                    <span className="text-brand">·</span>
                                    <span className="text-ink-100">{e.company}</span>
                                    {e.current && <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">CURRENT</span>}
                                </div>
                                <div className="mono-label">{e.period_start} — {e.current ? "Present" : e.period_end}</div>
                                <p className="mt-2 text-sm text-ink-200 line-clamp-2">{e.description}</p>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                                <button onClick={() => setEditing({ ...e })} data-testid={`exp-edit-${e.id}`} className="p-2 rounded hover:bg-white/10 text-ink-200 hover:text-white">
                                    <Pencil size={14} />
                                </button>
                                <button onClick={() => remove(e.id)} data-testid={`exp-delete-${e.id}`} className="p-2 rounded hover:bg-red-500/10 text-ink-200 hover:text-red-400">
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                    ))}
                    {items.length === 0 && <div className="p-12 text-center text-ink-300 border border-dashed border-white/10 rounded-xl">No experiences yet.</div>}
                </div>
            )}

            {editing && <ExpEditor initial={editing} onClose={() => setEditing(null)} onSaved={load} />}
        </div>
    );
}

function ExpEditor({ initial, onClose, onSaved }) {
    const [data, setData] = useState(initial);
    const [saving, setSaving] = useState(false);
    const isEdit = Boolean(initial.id);
    const update = (k) => (e) => {
        const v = e.target.type === "checkbox" ? e.target.checked : e.target.type === "number" ? Number(e.target.value) : e.target.value;
        setData((s) => ({ ...s, [k]: v }));
    };
    const updateLines = (k) => (e) => {
        const v = e.target.value.split("\n").map((s) => s.trim()).filter(Boolean);
        setData((s) => ({ ...s, [k]: v }));
    };
    const updateCSV = (k) => (e) => {
        const v = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
        setData((s) => ({ ...s, [k]: v }));
    };

    const save = async () => {
        setSaving(true);
        try {
            if (isEdit) await api.put(`/admin/experiences/${initial.id}`, data);
            else await api.post("/admin/experiences", data);
            toast.success(isEdit ? "Experience updated" : "Experience created");
            onSaved();
            onClose();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/70 backdrop-blur" data-testid="exp-editor">
            <div className="w-full max-w-2xl bg-ink-900 border-l border-white/10 overflow-y-auto">
                <div className="sticky top-0 z-10 bg-ink-900/95 backdrop-blur border-b border-white/5 px-6 py-4 flex items-center justify-between">
                    <div>
                        <div className="mono-label">{isEdit ? "Edit experience" : "New experience"}</div>
                        <h2 className="font-display text-xl text-white">{data.role || "Untitled"}</h2>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={save} disabled={saving} data-testid="exp-save" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand text-white hover:bg-brand-hover">
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                        </button>
                        <button onClick={onClose} className="p-2 rounded-md hover:bg-white/10 text-ink-200"><X size={16} /></button>
                    </div>
                </div>
                <div className="p-6 space-y-5">
                    <Card title="Role">
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Company"><input className="ainp" value={data.company} onChange={update("company")} /></Field>
                            <Field label="Role"><input className="ainp" value={data.role} onChange={update("role")} /></Field>
                        </div>
                        <Field label="Location"><input className="ainp" value={data.location} onChange={update("location")} /></Field>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Period start"><input className="ainp" value={data.period_start} onChange={update("period_start")} placeholder="Jan 2023" /></Field>
                            <Field label="Period end"><input className="ainp" value={data.period_end} onChange={update("period_end")} placeholder="Present" disabled={data.current} /></Field>
                        </div>
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input type="checkbox" checked={data.current} onChange={update("current")} className="w-4 h-4 accent-brand" />
                            <span className="text-sm text-ink-100">This is my current role</span>
                        </label>
                        <Field label="Description"><textarea rows={5} className="ainp" value={data.description} onChange={update("description")} /></Field>
                    </Card>

                    <Card title="Impact">
                        <Field label="Achievements (one per line)">
                            <textarea rows={5} className="ainp" value={(data.achievements || []).join("\n")} onChange={updateLines("achievements")} placeholder="Led migration that reduced latency 60%" />
                        </Field>
                        <Field label="Tech (comma-separated)">
                            <input className="ainp" value={(data.tech || []).join(", ")} onChange={updateCSV("tech")} placeholder="React, Node.js, AWS" />
                        </Field>
                        <Field label="Order"><input type="number" className="ainp" value={data.order} onChange={update("order")} /></Field>
                    </Card>
                </div>
            </div>
        </div>
    );
}
